import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";

export const useProperties = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["properties", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // CRITICAL SECURITY: Always filter by owner_id
      // Defense in depth - RLS also enforces this, but we add explicit filter
      // ROOT CAUSE: Multi-tenant isolation bug - owners could see other owners' properties
      // SOLUTION: Explicit owner_id filter + runtime assertion to catch any RLS bypass
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("owner_id", user.id)
        .is("archived_at", null)
        .is("deleted_at", null);
      
      // CRITICAL SECURITY GUARDRAIL: Runtime assertion to catch cross-account leakage
      // If ANY property has owner_id != user.id, this is a security breach
      if (data) {
        const invalidProperties = data.filter(p => p.owner_id !== user.id);
        if (invalidProperties.length > 0) {
          console.error('[useProperties] SECURITY BREACH: Properties found with wrong owner_id!', {
            userId: user.id,
            invalidProperties: invalidProperties.map(p => ({ id: p.id, owner_id: p.owner_id, name: p.name }))
          });
          // Do NOT render invalid properties - filter them out
          return data.filter(p => p.owner_id === user.id);
        }
      }
      
      // Security sanity check: Log property count for verification (dev only)
      if (process.env.NODE_ENV === 'development' && data) {
        console.log(`[useProperties] Owner ${user.id} sees ${data.length} properties (all verified)`);
      }

      if (error) {
        console.error("[useProperties] Supabase error:", {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        });
        console.error("[useProperties] Full error object:", error);
        throw error;
      }
      
      // Handle empty arrays gracefully
      return data || [];
    },
    enabled: !!user?.id,
  });
};

export const useProperty = (propertyId: string | undefined) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["property", propertyId, user?.id],
    queryFn: async () => {
      if (!propertyId || !user?.id) return null;

      // CRITICAL SECURITY: Always filter by owner_id
      // Defense in depth - RLS also enforces this, but we add explicit filter
      // ROOT CAUSE: Multi-tenant isolation bug - owners could see other owners' properties
      // SOLUTION: Explicit owner_id filter + runtime assertion
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("id", propertyId)
        .eq("owner_id", user.id)
        .maybeSingle();

      // CRITICAL SECURITY GUARDRAIL: Runtime assertion to catch cross-account leakage
      if (data && data.owner_id !== user.id) {
        console.error(`[useProperty] SECURITY BREACH: Property ${propertyId} owner mismatch!`, {
          userId: user.id,
          propertyOwnerId: data.owner_id,
          propertyId,
          propertyName: data.name,
        });
        return null;
      }

      if (process.env.NODE_ENV === "development" && data) {
        console.log(`[useProperty] Owner ${user.id} accessed property ${propertyId} (verified)`);
      }

      if (error) throw error;
      return data;
    },
    enabled: !!propertyId && !!user?.id,
  });
};

/**
 * Fetch property for current user when they have access (owner or co-host).
 * Owned properties: filter by owner_id. Co-hosted: fetch by id (RLS enforces access).
 */
export const usePropertyWithAccess = (propertyId: string | undefined) => {
  const { user, profile } = useAuth();
  const { is_cohost } = useUserCapabilities();

  return useQuery({
    queryKey: ["property-with-access", propertyId, user?.id, profile?.role],
    queryFn: async () => {
      if (!propertyId || !user?.id) return null;

      const { data: owned, error: ownedError } = await supabase
        .from("properties")
        .select("*")
        .eq("id", propertyId)
        .eq("owner_id", user.id)
        .is("deleted_at", null)
        .maybeSingle();
      if (ownedError) throw ownedError;
      if (owned) return owned;

      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("id", propertyId)
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!propertyId && !!user?.id && (profile?.role !== "cleaner" || is_cohost),
  });
};

export interface CreatePropertyData {
  name: string;
  address?: string;
  city?: string;
  state_province?: string;
  postal_code?: string;
  country?: string;
  formatted_address?: string;
  latitude?: number | null;
  longitude?: number | null;
  hospitable_property_id?: string | null;
}

export const useCreateProperty = () => {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();

  return useMutation({
    mutationFn: async (data: CreatePropertyData) => {
      if (!user) throw new Error("Not authenticated");
      if (profile?.role === "pending_invite") {
        throw new Error("Accept your invite before adding a property");
      }

      // Create property: DO NOT set archived_at or deleted_at
      // They default to NULL in the database
      const { data: property, error } = await supabase
        .from("properties")
        .insert({
          name: data.name,
          address: data.address || null,
          city: data.city || null,
          state_province: data.state_province || null,
          postal_code: data.postal_code || null,
          country: data.country || null,
          formatted_address: data.formatted_address || null,
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
          owner_id: user.id,
          ...(data.hospitable_property_id
            ? { hospitable_property_id: data.hospitable_property_id }
            : {}),
        })
        .select()
        .single();

      if (error) {
        console.error("[useCreateProperty] Supabase error:", {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        });
        console.error("[useCreateProperty] Full error object:", error);
        throw error;
      }
      return property;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["user-capabilities"] });
      queryClient.invalidateQueries({ queryKey: ["access-resolver"] });
    },
  });
};

export const useUpdateProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      name,
      address,
      unit,
      city,
      state_province,
      postal_code,
      country,
      formatted_address,
      latitude,
      longitude,
      notes,
      property_photo_url,
      property_photo_urls,
      owner_only_assignment,
      inventory_reimbursement_mode,
      billing_mode,
      inventory_autopay_enabled,
      default_checkin_time,
      default_checkout_time,
      turnover_offset_minutes,
      default_checklist_id,
      default_assignment_mode,
      preferred_cleaner_id,
      hospitable_property_id,
      amazon_buy_list_url,
    }: {
      propertyId: string;
      name?: string;
      address?: string | null;
      unit?: string | null;
      city?: string | null;
      state_province?: string | null;
      postal_code?: string | null;
      country?: string | null;
      formatted_address?: string | null;
      latitude?: number | null;
      longitude?: number | null;
      notes?: string | null;
      property_photo_url?: string | null;
      owner_only_assignment?: boolean;
      inventory_reimbursement_mode?: string;
      billing_mode?: "solo" | "company" | "track_only" | "self_managed";
      inventory_autopay_enabled?: boolean;
      default_checkin_time?: string | null;
      default_checkout_time?: string | null;
      turnover_offset_minutes?: number;
      default_checklist_id?: string | null;
      default_assignment_mode?: "manual" | "rotation" | "preferred_cleaner";
      preferred_cleaner_id?: string | null;
      property_photo_urls?: string[] | null;
      hospitable_property_id?: string | null;
      amazon_buy_list_url?: string | null;
    }) => {
      const updateData: Record<string, unknown> = {};
      if (name !== undefined) updateData.name = name;
      if (address !== undefined) updateData.address = address;
      if (unit !== undefined) updateData.unit = unit;
      if (city !== undefined) updateData.city = city;
      if (state_province !== undefined) updateData.state_province = state_province;
      if (postal_code !== undefined) updateData.postal_code = postal_code;
      if (country !== undefined) updateData.country = country;
      if (formatted_address !== undefined) updateData.formatted_address = formatted_address;
      if (latitude !== undefined) updateData.latitude = latitude;
      if (longitude !== undefined) updateData.longitude = longitude;
      if (notes !== undefined) updateData.notes = notes;
      if (property_photo_url !== undefined) updateData.property_photo_url = property_photo_url;
      if (property_photo_urls !== undefined) updateData.property_photo_urls = property_photo_urls ?? [];
      if (owner_only_assignment !== undefined) updateData.owner_only_assignment = owner_only_assignment;
      if (inventory_reimbursement_mode !== undefined) updateData.inventory_reimbursement_mode = inventory_reimbursement_mode;
      if (billing_mode !== undefined) updateData.billing_mode = billing_mode;
      if (inventory_autopay_enabled !== undefined) updateData.inventory_autopay_enabled = inventory_autopay_enabled;
      if (default_checkin_time !== undefined) updateData.default_checkin_time = default_checkin_time;
      if (default_checkout_time !== undefined) updateData.default_checkout_time = default_checkout_time;
      if (turnover_offset_minutes !== undefined) updateData.turnover_offset_minutes = turnover_offset_minutes;
      if (default_checklist_id !== undefined) updateData.default_checklist_id = default_checklist_id;
      if (default_assignment_mode !== undefined) updateData.default_assignment_mode = default_assignment_mode;
      if (preferred_cleaner_id !== undefined) updateData.preferred_cleaner_id = preferred_cleaner_id;
      if (hospitable_property_id !== undefined) {
        const hid = (hospitable_property_id || "").trim();
        updateData.hospitable_property_id = hid || null;
      }
      if (amazon_buy_list_url !== undefined) {
        const url = (amazon_buy_list_url || "").trim();
        updateData.amazon_buy_list_url = url || null;
      }

      let result = await supabase
        .from("properties")
        .update(updateData)
        .eq("id", propertyId)
        .select()
        .single();

      // If DB doesn't have property_photo_urls yet (migration not run), retry without it
      if (result.error && (result.error.code === "42703" || String(result.error.message).includes("property_photo_urls"))) {
        const { property_photo_urls: _urls, ...fallbackData } = updateData as Record<string, unknown> & { property_photo_urls?: unknown };
        result = await supabase
          .from("properties")
          .update(fallbackData)
          .eq("id", propertyId)
          .select()
          .single();
      }

      if (result.error) throw result.error;
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["property", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["property-with-access", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["property-cleaner", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["current-cleaning-task", variables.propertyId] });
    },
  });
};

export type PropertyAccessInstructions = {
  entry_method: string | null;
  entry_code: string | null;
  entry_location: string | null;
  unit_location: string | null;
  parking_instructions: string | null;
  supply_closet_info: string | null;
  access_notes: string | null;
  key_location_reference_urls?: string[] | null;
};

export const useUpdatePropertyAccessInstructions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      ...fields
    }: {
      propertyId: string;
      entry_method?: string | null;
      entry_code?: string | null;
      entry_location?: string | null;
      unit_location?: string | null;
      parking_instructions?: string | null;
      supply_closet_info?: string | null;
      access_notes?: string | null;
      key_location_reference_urls?: string[] | null;
    }) => {
      const { data, error } = await supabase.rpc("update_property_keys_and_entry", {
        p_property_id: propertyId,
        p_entry_method: fields.entry_method ?? null,
        p_entry_code: fields.entry_code ?? null,
        p_set_entry_code: fields.entry_code !== undefined,
        p_entry_location: fields.entry_location ?? null,
        p_unit_location: fields.unit_location ?? null,
        p_parking_instructions: fields.parking_instructions ?? null,
        p_supply_closet_info: fields.supply_closet_info ?? null,
        p_access_notes: fields.access_notes ?? null,
        p_key_location_reference_urls: fields.key_location_reference_urls ?? null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.setQueriesData(
          { queryKey: ["property-with-access", variables.propertyId] },
          (old: unknown) => (old && typeof old === "object" ? { ...(old as object), ...(data as object) } : data)
        );
      }
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["property", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["property-with-access", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["property-cleaner", variables.propertyId] });
    },
  });
};
